import React from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Key, Lock, Check, AlertTriangle, Settings, RefreshCw, Loader2 } from 'lucide-react';

export const SsoTab = ({
  ssoConfig, ssoForm, setSsoForm,
  onSave, savingSso,
  onTest, testingSso, ssoTestResult,
  campus,
}) => (
  <Card className="p-6  bg-[var(--isp-glass)] backdrop-blur-xl border-[var(--isp-glass-border)]" data-testid="campus-sso">
    <h3 className="text-lg font-semibold text-[var(--isp-heading)] mb-4 flex items-center gap-2">
      <Key className="w-5 h-5 text-amber-400" />
      Single Sign-On (SSO)
    </h3>

    {!ssoConfig?.enabled && (
      <div className="text-center py-8">
        <Lock className="w-12 h-12 mx-auto text-white/20 mb-4" />
        <p className="text-[var(--isp-body)] mb-2">SSO is available on Enterprise plan only</p>
        <p className="text-xs text-[var(--isp-muted)]">Current plan: {ssoConfig?.current_plan || campus.plan_id}</p>
      </div>
    )}

    {ssoConfig?.enabled && (
      <div className="space-y-4">
        <p className="text-sm text-[var(--isp-body)]">
          Configure your identity provider to allow campus members to sign in with institutional credentials.
        </p>

        {ssoConfig.callback_url && (
          <div className="p-3 rounded-xl bg-blue-500/10 border border-blue-500/20">
            <p className="text-xs text-[var(--isp-body)] mb-1">Callback URL (add to your IdP)</p>
            <p className="text-sm text-blue-300 font-mono break-all">{ssoConfig.callback_url}</p>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-[var(--isp-body)] block mb-1">SSO Provider</label>
            <select
              value={ssoForm.sso_provider}
              onChange={e => setSsoForm({ ...ssoForm, sso_provider: e.target.value })}
              className="w-full p-3 rounded-xl bg-[var(--isp-glass-inner)] border border-[var(--isp-glass-border)] text-white text-sm"
              data-testid="sso-provider-select"
            >
              <option value="saml">SAML 2.0</option>
              <option value="oidc">OpenID Connect (OIDC)</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-[var(--isp-body)] block mb-1">Entity ID / Client ID</label>
            <input
              value={ssoForm.entity_id}
              onChange={e => setSsoForm({ ...ssoForm, entity_id: e.target.value })}
              placeholder="https://idp.university.edu/entity"
              className="w-full p-3 rounded-xl bg-[var(--isp-glass-inner)] border border-[var(--isp-glass-border)] text-white text-sm"
              data-testid="sso-entity-id"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs text-[var(--isp-body)] block mb-1">SSO Login URL</label>
            <input
              value={ssoForm.sso_url}
              onChange={e => setSsoForm({ ...ssoForm, sso_url: e.target.value })}
              placeholder="https://idp.university.edu/sso/saml"
              className="w-full p-3 rounded-xl bg-[var(--isp-glass-inner)] border border-[var(--isp-glass-border)] text-white text-sm"
              data-testid="sso-url-input"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs text-[var(--isp-body)] block mb-1">Certificate (PEM format)</label>
            <textarea
              value={ssoForm.certificate}
              onChange={e => setSsoForm({ ...ssoForm, certificate: e.target.value })}
              placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
              rows={4}
              className="w-full p-3 rounded-xl bg-[var(--isp-glass-inner)] border border-[var(--isp-glass-border)] text-white text-sm resize-none font-mono"
              data-testid="sso-certificate"
            />
          </div>
          <div>
            <label className="text-xs text-[var(--isp-body)] block mb-1">Domain Restriction (optional)</label>
            <input
              value={ssoForm.domain_restriction}
              onChange={e => setSsoForm({ ...ssoForm, domain_restriction: e.target.value })}
              placeholder="university.edu"
              className="w-full p-3 rounded-xl bg-[var(--isp-glass-inner)] border border-[var(--isp-glass-border)] text-white text-sm"
              data-testid="sso-domain"
            />
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={ssoForm.auto_provision}
                onChange={e => setSsoForm({ ...ssoForm, auto_provision: e.target.checked })}
                className="w-4 h-4 rounded"
                data-testid="sso-auto-provision"
              />
              <span className="text-sm text-[var(--isp-body)]">Auto-provision new users</span>
            </label>
          </div>
        </div>

        <div className="flex gap-3">
          <Button
            onClick={onSave}
            disabled={savingSso}
            className="rounded-xl gap-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
            data-testid="save-sso-btn"
          >
            {savingSso ? <Loader2 className="w-4 h-4 animate-spin" /> : <Settings className="w-4 h-4" />}
            Save Configuration
          </Button>
          <Button
            onClick={onTest}
            disabled={testingSso}
            variant="outline"
            className="rounded-xl gap-2 border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10"
            data-testid="test-sso-btn"
          >
            {testingSso ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Test Connection
          </Button>
        </div>

        {ssoTestResult && <SsoTestResult result={ssoTestResult} />}

        {ssoConfig.configured && (
          <div className="p-3 rounded-xl bg-green-500/10 border border-green-500/20 flex items-center gap-2">
            <Check className="w-4 h-4 text-green-400" />
            <span className="text-sm text-green-300">SSO is configured and active</span>
          </div>
        )}
      </div>
    )}
  </Card>
);

const SsoTestResult = ({ result }) => {
  const wrapperCls = result.success
    ? 'bg-green-500/10 border-green-500/20'
    : 'bg-amber-500/10 border-amber-500/20';
  const textCls = result.success ? 'text-green-300' : 'text-amber-300';
  const Icon = result.success ? Check : AlertTriangle;

  return (
    <div className={`p-4 rounded-xl border ${wrapperCls}`} data-testid="sso-test-result">
      <p className={`text-sm font-medium mb-2 ${textCls}`}>
        <Icon className="w-4 h-4 inline mr-1" />
        {result.message}
      </p>
      {result.checks && (
        <div className="space-y-1 text-xs text-[var(--isp-body)]">
          <p>{result.checks.url_configured ? '✓' : '✗'} SSO URL configured</p>
          <p>
            {result.checks.url_reachable ? '✓' : '✗'} SSO URL reachable
            {result.checks.response_status ? ` (HTTP ${result.checks.response_status})` : ''}
          </p>
          <p>{result.checks.entity_id_set ? '✓' : '✗'} Entity ID set</p>
          <p>{result.checks.certificate_set ? '✓' : '✗'} Certificate set</p>
          {result.checks.domain_restriction && <p>Domain: {result.checks.domain_restriction}</p>}
          {result.checks.error && <p className="text-red-400">Error: {result.checks.error}</p>}
        </div>
      )}
    </div>
  );
};
